import { WarningFilled } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Form, Input, Tooltip } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../contexts/auth/useAuthContext";
import { useToast } from "../contexts/toast/useToast";
import { useUpdateUserEmailMutation } from "../graphql/mutation.updateUserEmail";
import { useUpdateUserProfileMutation } from "../graphql/mutation.updateUserProfile";
import GlobalLoader from "./GlobalLoader";

const ProfilePage = () => {
  const { user, updateUser, logout } = useAuthContext();

  const { open } = useToast();
  const { t } = useTranslation();

  const [identityForm] = Form.useForm();
  const [emailForm] = Form.useForm();

  const [
    updateUserProfile,
    { loading: updateProfileLoading, error: updateProfileError },
  ] = useUpdateUserProfileMutation();

  const [
    updateUserEmail,
    { loading: updateEmailLoading, error: updateEmailError },
  ] = useUpdateUserEmailMutation();


  useEffect(() => {
    if (user) {
      identityForm.setFieldsValue({
        name: user.name || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
      });
      emailForm.setFieldsValue({
        email: user.email,
      });
    }
  }, [user, identityForm, emailForm]);

  if (!user) {
    return <GlobalLoader />;
  }

  const isSSOUser = user ? !!user.socialProvider : false;
  
  const onIdentityFormFinish = async (values: {
    name: string;
    firstName: string;
    lastName: string;
  }) => {
    if (!user || isSSOUser) return;
    try {
      const response = await updateUserProfile({
        variables: {
          input: {
            name: values.name,
            firstName: values.firstName,
            lastName: values.lastName,
          },
        },
      });
      open("success", t("account_updated_successfully"));
      updateUser(response.data!.updateUserProfile);
    } catch (err) {
      open("error", t("account_update_failed"));
      console.error("Error updating profile:", err);
    }
  };

  const onEmailFormFinish = async (values: { email: string }) => {
    if (!user || isSSOUser) return;
    try {
      await updateUserEmail({
        variables: {
          input: {
            email: values.email,
          },
        },
      });
      open("success", t("account_email_updated_successfully"));
      logout();
    } catch (err) {
      open("error", t("account_email_update_failed"));
      console.error("Error updating profile:", err);
    }
  };

  return (
    <Flex vertical gap={16} style={{ maxWidth: 600, margin: "0 auto" }}>
      {!isSSOUser && (
        <>
          {updateEmailError && (
            <Alert
              message={t("account_error_updating_email")}
              description={updateEmailError.message}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Card title={t("account_email_title")}>
            <Alert
              message={t("account_email_warning")}
              type="error"
              style={{ marginBottom: 16 }}
              showIcon
              icon={<WarningFilled />}
            />
            <Form
              form={emailForm}
              layout="vertical"
              onFinish={onEmailFormFinish}
              disabled={updateEmailLoading}
              initialValues={{
                email: user.email || "",
              }}
            >
              <Form.Item
                label={t("account_email_label")}
                name="email"
                rules={[
                  {
                    required: true,
                    message: t("account_email_required"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item style={{ justifyItems: "flex-end" }}>
                <Tooltip title={t("account_email_warning")}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateEmailLoading}
                    icon={<WarningFilled />}
                  >
                    {updateEmailLoading
                      ? t("account_saving")
                      : t("account_save_changes")}
                  </Button>
                </Tooltip>
              </Form.Item>
            </Form>
          </Card>
        </>
      )}

      {isSSOUser && (
        <Flex style={{ marginBottom: 16 }} align="center" gap={8}>
          <img
            src={`/images/providers/${user.socialProvider}.svg`}
            alt={user.socialProvider}
            style={{ width: 24, height: 24 }}
          />
          <Alert
            message={t("connected_with_label", {
              provider: t(`login_method_${user.socialProvider}`),
              email: user.email,
            })}
            type="warning"
          />
        </Flex>
      )}

      {updateProfileError && (
        <Alert
          message={t("error_updating_profile")}
          description={updateProfileError.message}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Card title={t("account_page_title")}>
        <Form
          form={identityForm}
          layout="vertical"
          onFinish={onIdentityFormFinish}
          disabled={isSSOUser || updateProfileLoading}
          initialValues={{
            name: user.name || "",
            firstName: user.firstName || "",
            lastName: user.lastName || "",
          }}
        >
          <Form.Item
            label={t("account_name_label")}
            name="name"
            rules={[
              {
                required: true,
                message: t("account_name_required"),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("account_first_name_label")}
            name="firstName"
            rules={[
              {
                required: true,
                message: t("account_first_name_required"),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("account_last_name_label")}
            name="lastName"
            rules={[
              {
                required: true,
                message: t("account_last_name_required"),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item style={{ justifyItems: "flex-end" }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateProfileLoading}
            >
              {updateProfileLoading
                ? t("account_saving")
                : t("account_save_changes")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default ProfilePage;
